import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Building2, Activity, Users, ArrowRight, Loader2, MapPin, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Checkbox } from '../components/ui/checkbox';

const SelectRole = () => {
  const navigate = useNavigate();
  const { user, setUser, checkAuth, loading } = useAuth();
  const [selectedRole, setSelectedRole] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    cnpj: '',
    cep: '',
    street: '',
    number: '',
    neighborhood: '',
    city: '',
    state: '',
    phone: '',
    accepted_plans: ['basic', 'plus', 'premium']
  });

  // Redirect if not logged in or already has role
  React.useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/'); return; }
    if (user.role) {
      const dashboardMap = { rh: '/rh', gym: '/gym', student: '/student' };
      navigate(dashboardMap[user.role]);
    }
  }, [user, loading, navigate]);

  const roles = [
    {
      id: 'rh',
      title: 'RH da Empresa',
      description: 'Gerencie a saúde corporativa dos seus colaboradores',
      icon: <Building2 className="w-8 h-8" />,
      color: 'bg-blue-600',
      lightColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      requiresSetup: true
    },
    {
      id: 'gym',
      title: 'Academia Parceira',
      description: 'Cadastre alunos e registre avaliações físicas',
      icon: <Activity className="w-8 h-8" />,
      color: 'bg-orange-500',
      lightColor: 'bg-orange-50',
      textColor: 'text-orange-500',
      requiresSetup: true
    }
  ];

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    if (role.requiresSetup) {
      setShowDialog(true);
    } else {
      handleSetRole(role.id);
    }
  };

  const handleSetRole = async (roleId) => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/set-role?role=${roleId}`,
        {}
      );
      
      await checkAuth();
      
      const dashboardMap = {
        rh: '/rh',
        gym: '/gym',
        student: '/student'
      };
      
      navigate(dashboardMap[roleId]);
    } catch (error) {
      console.error('Error setting role:', error);
      toast.error('Erro ao definir perfil');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetupSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (selectedRole.id === 'rh') {
        await axios.post(
          `${API}/companies`,
          { name: formData.name, cnpj: formData.cnpj }
        );
      } else if (selectedRole.id === 'gym') {
        await axios.post(
          `${API}/gyms`,
          {
            name: formData.name,
            cep: formData.cep || null,
            street: formData.street || null,
            number: formData.number || null,
            neighborhood: formData.neighborhood || null,
            city: formData.city || null,
            state: formData.state || null,
            phone: formData.phone || null,
            accepted_plans: formData.accepted_plans
          }
        );
      }

      await checkAuth();
      setShowDialog(false);
      
      // Redirect: RH goes to plan selection, Gym goes to dashboard
      if (selectedRole.id === 'rh') {
        navigate('/plans/company');
      } else {
        navigate('/gym');
      }
    } catch (error) {
      console.error('Error creating organization:', error);
      toast.error('Erro ao criar organização');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <Toaster position="top-center" />
      
      <div className="max-w-4xl w-full">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Activity className="w-10 h-10 text-blue-600" />
            <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Vital
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Selecione seu Perfil
          </h1>
          <p className="text-slate-600 text-lg">
            Olá, {user?.name}! Como você deseja usar a plataforma?
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {roles.map((role) => (
            <button
              key={role.id}
              onClick={() => handleRoleSelect(role)}
              data-testid={`role-${role.id}`}
              className="bg-white border border-slate-200 rounded-2xl p-8 text-left card-hover group focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <div className={`w-16 h-16 ${role.color} rounded-xl flex items-center justify-center text-white mb-6 transition-transform group-hover:scale-110`}>
                {role.icon}
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {role.title}
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                {role.description}
              </p>
              <div className={`inline-flex items-center gap-2 ${role.textColor} font-medium text-sm`}>
                Selecionar
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Setup Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              {selectedRole?.id === 'rh' ? 'Cadastrar Empresa' : 'Cadastrar Academia'}
            </DialogTitle>
            <DialogDescription>
              {selectedRole?.id === 'rh' 
                ? 'Informe os dados da sua empresa para começar'
                : 'Informe os dados da sua academia para começar'
              }
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSetupSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                {selectedRole?.id === 'rh' ? 'Nome da Empresa' : 'Nome da Academia'}
              </Label>
              <Input
                id="name"
                data-testid="setup-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={selectedRole?.id === 'rh' ? 'Ex: Tech Corp' : 'Ex: Academia Força'}
                required
              />
            </div>

            {selectedRole?.id === 'rh' && (
              <div className="space-y-2">
                <Label htmlFor="cnpj">CNPJ (opcional)</Label>
                <Input
                  id="cnpj"
                  data-testid="setup-cnpj-input"
                  value={formData.cnpj}
                  onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                  placeholder="00.000.000/0001-00"
                />
              </div>
            )}

            {selectedRole?.id === 'gym' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="cep">CEP *</Label>
                    <Input
                      id="cep"
                      data-testid="setup-cep-input"
                      value={formData.cep}
                      onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                      placeholder="00000-000"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefone</Label>
                    <Input
                      id="phone"
                      data-testid="setup-phone-input"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(00) 0000-0000"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="street">Rua / Logradouro *</Label>
                  <Input
                    id="street"
                    data-testid="setup-street-input"
                    value={formData.street}
                    onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                    placeholder="Ex: Rua das Flores"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="number">Número *</Label>
                    <Input
                      id="number"
                      data-testid="setup-number-input"
                      value={formData.number}
                      onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                      placeholder="123"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="neighborhood">Bairro</Label>
                    <Input
                      id="neighborhood"
                      data-testid="setup-neighborhood-input"
                      value={formData.neighborhood}
                      onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                      placeholder="Centro"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="city">Cidade</Label>
                    <Input
                      id="city"
                      data-testid="setup-city-input"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="São Paulo"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">Estado</Label>
                    <Input
                      id="state"
                      data-testid="setup-state-input"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      placeholder="SP"
                      maxLength={2}
                    />
                  </div>
                </div>

                <div className="space-y-3 pt-2">
                  <Label className="text-sm font-semibold">Planos de aluno aceitos pela academia</Label>
                  <div className="space-y-2">
                    {[
                      { id: 'basic', label: 'Vital Fit (R$ 89,90/mês)' },
                      { id: 'plus', label: 'Vital Plus (R$ 129,90/mês)' },
                      { id: 'premium', label: 'Vital Premium (R$ 149,90/mês)' }
                    ].map(plan => (
                      <div key={plan.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`plan-${plan.id}`}
                          data-testid={`plan-checkbox-${plan.id}`}
                          checked={formData.accepted_plans.includes(plan.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({ ...formData, accepted_plans: [...formData.accepted_plans, plan.id] });
                            } else {
                              setFormData({ ...formData, accepted_plans: formData.accepted_plans.filter(p => p !== plan.id) });
                            }
                          }}
                        />
                        <label htmlFor={`plan-${plan.id}`} className="text-sm text-slate-700 cursor-pointer">{plan.label}</label>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                data-testid="setup-submit-btn"
                disabled={submitting || !formData.name}
                className="flex-1 bg-slate-900 hover:bg-slate-800"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Continuar'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SelectRole;
